import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';

export default function EmailVerifiedPage() {
  const { supabase } = useAuth();
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('Processing email verification...');
  const navigate = useNavigate();

  useEffect(() => {
    const handleEmailVerification = async () => {
      try {
        // Get the URL query parameters (new approach)
        const urlParams = new URLSearchParams(window.location.search);
        const tokenHash = urlParams.get('token_hash');
        const type = urlParams.get('type');
        const redirectTo = urlParams.get('redirect_to');
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');

        console.log('[EmailVerified] Processing verification:', { 
          hasTokenHash: !!tokenHash, 
          type,
          redirectTo,
          error, 
          errorDescription 
        });

        // Check for errors first
        if (error) {
          console.error('[EmailVerified] Verification error:', error, errorDescription);
          setStatus('error');
          setMessage('Email verification failed. The link may have expired or is invalid.');
          return;
        }

        // If we have token hash, verify with Supabase
        if (tokenHash && type) {
          console.log('[EmailVerified] Verifying with token hash...');
          
          const { data, error: verifyError } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: type === 'signup' ? 'email' : 'email'
          });

          if (verifyError) {
            console.error('[EmailVerified] Error verifying token:', verifyError);
            setStatus('error');
            setMessage('Failed to verify email. The link may have expired or is invalid.');
            return;
          }

          if (data.user) {
            console.log('[EmailVerified] Email verification successful, user:', data.user);
            setStatus('success');
            
            if (type === 'signup') {
              setMessage('Email verified successfully! Your account is now active.');
            } else {
              setMessage('Email verified successfully!');
            }
          } else {
            setStatus('error');
            setMessage('Verification completed but user data is missing. Please try logging in manually.');
          }
        } else {
          // No token hash found, might be an invalid link
          console.warn('[EmailVerified] No token hash found in URL');
          setStatus('error');
          setMessage('Invalid verification link. Please check your email for the correct link.');
        }
      } catch (err) {
        console.error('[EmailVerified] Unexpected error during email verification:', err);
        setStatus('error');
        setMessage('An unexpected error occurred during verification. Please try logging in manually.');
      }
    };

    handleEmailVerification();
  }, [supabase]);

  const handleGoToDashboard = () => {
    navigate('/', { replace: true });
  };

  const handleGoToLogin = () => {
    navigate('/login', { replace: true });
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return (
          <div 
            className="spinner" 
            style={{ 
              width: 48, 
              height: 48, 
              border: '4px solid #eee', 
              borderTop: '4px solid #20b2aa', 
              borderRadius: '50%', 
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px auto'
            }} 
          />
        );
      case 'success':
        return (
          <svg 
            width="48" 
            height="48" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ margin: '0 auto 16px auto' }}
          >
            <circle cx="12" cy="12" r="10" fill="#20b2aa"/>
            <path d="m9 12 2 2 4-4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      case 'error':
        return (
          <svg 
            width="48" 
            height="48" 
            viewBox="0 0 24 24" 
            fill="none" 
            xmlns="http://www.w3.org/2000/svg"
            style={{ margin: '0 auto 16px auto' }}
          >
            <circle cx="12" cy="12" r="10" fill="#e74c3c"/>
            <path d="m15 9-6 6m0-6 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return '#20b2aa';
      case 'error':
        return '#e74c3c';
      default:
        return '#555';
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
      <div style={{ background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px #0001', padding: 40, width: 400, maxWidth: '90vw', display: 'flex', flexDirection: 'column', gap: 20, textAlign: 'center' }}>
        <img 
          src="https://amicredit.ro/wp-content/uploads/2024/02/amicredit.svg" 
          alt="AMI Credit" 
          style={{ width: 180, margin: '0 auto 12px auto' }} 
          onError={(e) => {
            e.target.style.display = 'none';
            const fallback = document.createElement('div');
            fallback.innerHTML = '<span style="color: #20b2aa; font-weight: 700; font-size: 24px;">Ami</span><span style="color: #374151; font-weight: 700; font-size: 24px;">Credit</span>';
            fallback.style.textAlign = 'center';
            fallback.style.margin = '0 auto 12px auto';
            e.target.parentNode.insertBefore(fallback, e.target);
          }}
        />
        
        {getStatusIcon()}
        
        <h2 style={{ margin: 0, fontWeight: 700, color: getStatusColor() }}>
          {status === 'processing' && 'Verifying Email'}
          {status === 'success' && 'Email Verified!'}
          {status === 'error' && 'Verification Failed'}
        </h2>
        
        <p style={{ margin: 0, color: '#555', lineHeight: 1.5 }}>
          {message}
        </p>
        
        {status === 'success' && (
          <button 
            onClick={handleGoToDashboard}
            style={{ 
              background: '#20b2aa', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 6, 
              padding: '12px 0', 
              fontSize: 17, 
              fontWeight: 600, 
              cursor: 'pointer',
              marginTop: 12
            }}
          >
            Go to Dashboard
          </button>
        )}
        
        {status === 'error' && (
          <button 
            onClick={handleGoToLogin}
            style={{ 
              background: '#e65c00', 
              color: '#fff', 
              border: 'none', 
              borderRadius: 6, 
              padding: '12px 0', 
              fontSize: 17, 
              fontWeight: 600, 
              cursor: 'pointer',
              marginTop: 12
            }}
          >
            Go to Login
          </button>
        )}
      </div>
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
